import React, { useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, ImagePlus } from "lucide-react";

/**
 * Image uploader. Props:
 *  - value: array of storage paths (e.g. "healthfairx/uploads/<uid>/<uuid>.jpg")
 *  - onChange: (paths: string[]) => void
 *  - max: maximum number of images allowed
 *  - label: optional label
 *  - testIdPrefix: unique prefix for data-testids
 */
export const ImageUpload = ({ value = [], onChange, max = 8, label = "Images", testIdPrefix = "img" }) => {
    const ref = useRef(null);
    const [busy, setBusy] = useState(false);

    const handleFiles = async (e) => {
        const fileList = Array.from(e.target.files || []);
        if (fileList.length === 0) return;
        if (value.length + fileList.length > max) {
            toast.error(`Max ${max} images`);
            return;
        }
        setBusy(true);
        try {
            const uploaded = [];
            for (const f of fileList) {
                if (f.size > 5 * 1024 * 1024) {
                    toast.error(`${f.name} is over 5MB`); continue;
                }
                const fd = new FormData();
                fd.append("file", f);
                const res = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                uploaded.push(res.data.path);
            }
            onChange([...value, ...uploaded]);
            if (uploaded.length) toast.success(`Uploaded ${uploaded.length} image${uploaded.length > 1 ? "s" : ""}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
            if (ref.current) ref.current.value = "";
        }
    };

    const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

    return (
        <div data-testid={`${testIdPrefix}-uploader`}>
            <label className="label-base">{label}</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {value.map((p, i) => (
                    <div key={p + i} className="relative group rounded-xl overflow-hidden border border-brand-line aspect-square bg-brand-bone">
                        <img src={imageUrl(p)} alt="" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => remove(i)} data-testid={`${testIdPrefix}-remove-${i}`}
                            className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/90 border border-brand-line text-brand-ink inline-flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                            <X size={14} />
                        </button>
                    </div>
                ))}
                {value.length < max && (
                    <button type="button" data-testid={`${testIdPrefix}-add`} onClick={() => ref.current?.click()} disabled={busy}
                        className="aspect-square rounded-xl border-2 border-dashed border-brand-line bg-white hover:bg-brand-bone hover:border-brand-forest flex flex-col items-center justify-center gap-1 text-brand-mute disabled:opacity-50 transition-colors">
                        {busy ? <Upload size={20} className="animate-pulse" /> : <ImagePlus size={20} />}
                        <span className="text-xs font-semibold">{busy ? "Uploading…" : "Add image"}</span>
                    </button>
                )}
            </div>
            <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple onChange={handleFiles} className="hidden" data-testid={`${testIdPrefix}-input`} />
            <p className="text-xs text-brand-mute mt-2">JPG / PNG / WebP / GIF, up to 5MB each. {value.length}/{max} used.</p>
        </div>
    );
};

/**
 * Single-image uploader (for logos). Same contract but `value` is a single path string.
 */
export const SingleImageUpload = ({ value, onChange, label = "Logo", testIdPrefix = "logo" }) => {
    const ref = useRef(null);
    const [busy, setBusy] = useState(false);

    const handle = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const res = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange(res.data.path);
            toast.success("Logo uploaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
            if (ref.current) ref.current.value = "";
        }
    };

    return (
        <div data-testid={`${testIdPrefix}-uploader`}>
            <label className="label-base">{label}</label>
            <div className="flex items-center gap-4">
                <div className="h-20 w-20 rounded-2xl border border-brand-line bg-brand-bone overflow-hidden flex items-center justify-center">
                    {value ? <img src={imageUrl(value)} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="text-brand-mute" size={20} />}
                </div>
                <div className="flex gap-2">
                    <button type="button" disabled={busy} data-testid={`${testIdPrefix}-add`} onClick={() => ref.current?.click()}
                        className="pill px-4 py-2 text-sm font-semibold border border-brand-line bg-white hover:bg-brand-bone disabled:opacity-50">
                        {busy ? "Uploading…" : value ? "Replace" : "Upload"}
                    </button>
                    {value && <button type="button" data-testid={`${testIdPrefix}-clear`} onClick={() => onChange("")} className="pill px-4 py-2 text-sm font-semibold text-brand-coral hover:bg-brand-coral/5">Remove</button>}
                </div>
            </div>
            <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handle} className="hidden" />
        </div>
    );
};

export const imageUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    // Root-relative URLs (e.g. `/images/healthfairx_logo.png` for assets bundled
    // in `frontend/public/`) are already canonical — let the browser resolve
    // them against the current origin. Backend-uploaded files use a relative
    // path WITHOUT a leading slash (e.g. `uploads/abc.png`) and route through
    // the file API below.
    if (path.startsWith("/")) return path;
    return `${API}/files/${path}`;
};
